/**
 * Compound components for subscription dialogs.
 *
 * @example
 * ```tsx
 * <SubscriptionDialogContent>
 *   <SubscriptionDialogBody>
 *     <SubscriptionDialogHeader>
 *       <img src={...} />
 *       <SubscriptionDialogHeading>
 *         <SubscriptionDialogTitle>...</SubscriptionDialogTitle>
 *         <SubscriptionDialogDescription>...</SubscriptionDialogDescription>
 *       </SubscriptionDialogHeading>
 *     </SubscriptionDialogHeader>
 *     <SubscriptionDialogSection>...</SubscriptionDialogSection>
 *     <SubscriptionDialogNotice>...</SubscriptionDialogNotice>
 *   </SubscriptionDialogBody>
 *   <SubscriptionDialogFooter>...</SubscriptionDialogFooter>
 * </SubscriptionDialogContent>
 * ```
 */

import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef } from 'react';

import {
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/shared/components/ui/custom/responsive-dialog';
import { cn } from '@/shared/utils/cn';

const gradientClassName =
  'bg-gradient-to-b from-[#28135c] via-[#170919] via-[31.25%] to-black';

export type SubscriptionDialogContentProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogContent({
  className,
  children,
  ...props
}: SubscriptionDialogContentProps) {
  return (
    <ResponsiveDialogContent
      className={cn('rounded-t-lg md:rounded-lg', gradientClassName, className)}
      {...props}
    >
      <ResponsiveDialogClose className="text-cold-200 absolute top-4 right-5 z-10 transition-opacity hover:opacity-80 md:hidden">
        <X className="size-5" />
        <span className="sr-only">Close</span>
      </ResponsiveDialogClose>
      {children}
    </ResponsiveDialogContent>
  );
}

export type SubscriptionDialogBodyProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogBody({
  className,
  children,
  ...props
}: SubscriptionDialogBodyProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-9 overflow-auto px-5 pt-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type SubscriptionDialogHeaderProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogHeader({
  className,
  children,
  ...props
}: SubscriptionDialogHeaderProps) {
  return (
    <ResponsiveDialogHeader
      className={cn('w-full items-center gap-4 p-0', className)}
      {...props}
    >
      {children}
    </ResponsiveDialogHeader>
  );
}

export type SubscriptionDialogHeadingProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogHeading({
  className,
  children,
  ...props
}: SubscriptionDialogHeadingProps) {
  return (
    <div
      className={cn('flex w-full flex-col items-center gap-3 px-4', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export type SubscriptionDialogTitleProps = ComponentPropsWithoutRef<'h2'>;

export function SubscriptionDialogTitle({
  className,
  children,
  ...props
}: SubscriptionDialogTitleProps) {
  return (
    <ResponsiveDialogTitle
      className={cn(
        'font-nunito text-center text-2xl leading-tight font-black text-white',
        className,
      )}
      {...props}
    >
      {children}
    </ResponsiveDialogTitle>
  );
}

export type SubscriptionDialogDescriptionProps = ComponentPropsWithoutRef<'p'>;

export function SubscriptionDialogDescription({
  className,
  children,
  ...props
}: SubscriptionDialogDescriptionProps) {
  return (
    <ResponsiveDialogDescription
      className={cn(
        'font-nunito text-cold-300 text-center text-base font-bold',
        className,
      )}
      {...props}
    >
      {children}
    </ResponsiveDialogDescription>
  );
}

export type SubscriptionDialogSectionProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogSection({
  className,
  children,
  ...props
}: SubscriptionDialogSectionProps) {
  return (
    <div className={cn('flex w-full flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
}

export type SubscriptionDialogNoticeProps = ComponentPropsWithoutRef<'p'>;

export function SubscriptionDialogNotice({
  className,
  children,
  ...props
}: SubscriptionDialogNoticeProps) {
  return (
    <p
      className={cn(
        'font-nunito text-center text-base font-semibold text-white',
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

export type SubscriptionDialogFooterProps = ComponentPropsWithoutRef<'div'>;

export function SubscriptionDialogFooter({
  className,
  children,
  ...props
}: SubscriptionDialogFooterProps) {
  return (
    <ResponsiveDialogFooter
      className={cn('flex w-full items-center gap-2 px-4 py-6', className)}
      {...props}
    >
      {children}
    </ResponsiveDialogFooter>
  );
}
